import {
  EntityType,
  ExportEntitiesCommand,
  Locale,
} from '@amzn/awsdev-docs-virtual-smiley-typescript-client';
import { Button, StatusIndicator } from '@amzn/awsui-components-react';
import { ReactElement, useCallback, useState } from 'react';
import { useAuthState } from '../authentication';
import { callApi } from '../client';
import { NoTokenError } from '../errors';

type ExportStatus = 'success' | 'loading' | 'error' | 'idle';
const texts = {
  success: 'Exported',
  loading: 'Loading',
  error: 'Error',
};

export const ExportEntityList = ({
  entityType,
  locale,
}: {
  entityType: EntityType;
  locale: Locale;
}): ReactElement => {
  const { token } = useAuthState();
  const [status, setStatus] = useState<ExportStatus>('idle');
  const reset = useCallback(() => setStatus('idle'), []);

  const download = useCallback(() => {
    if (!token) throw new NoTokenError();
    setStatus('loading');
    callApi(new ExportEntitiesCommand({ entityType, locale }), token)
      .then((res) => {
        const text = new TextDecoder().decode(res.output);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(
          new Blob([text], { type: 'text/csv;charset=utf-8;' })
        );
        a.setAttribute('target', '_blank');
        const fileName =
          entityType === EntityType.SHARED_TEXT
            ? 'shared_text_entities'
            : 'service_entities';
        a.download = `${fileName}_${
          new Date().toISOString().split('.')[0]
        }.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        setStatus('success');
        setTimeout(reset, 2000);
      })
      .catch((e) => {
        setStatus('error');
        console.log('Error exporting entities', e);
        setTimeout(reset, 3000);
      });
  }, [token, entityType, locale]);

  return (
    <Button onClick={download} disabled={status !== 'idle'}>
      {status !== 'idle' ? (
        <StatusIndicator type={status}>{texts[status]}</StatusIndicator>
      ) : (
        'Export all'
      )}
    </Button>
  );
};
